import { INFINITE_QUERY_LIMIT } from '@/components/graphqlTable';
import { InputMaybe } from '@/generated/graphql';
import { useCompanyId } from '@/providers/auth/useSessionStorageCompanyId';
import { useEvents } from '@/providers/event';
import type { Maybe, Query } from '@/types/schema';
import { DocumentNode, OperationVariables } from '@apollo/client';
import type { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import stringify from 'fast-json-stable-stringify';
import { map } from 'fp-ts/Array';
import { identity, pipe } from 'fp-ts/function';
import uniqByFp from 'lodash/fp/uniqBy';
import { flatten, isEmpty, isNil, omitBy } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { queryGraphQL } from './apollo';

interface PageData {
	count?: number,
	items?: any[]
}

export interface InfiniteQuery {
	data: Query,
	pageParam?: number
}

// TODO: Remove this hardcoded error message
export const TEMP_HARDCODED_ERROR = 'Something went wrong! Please try again or refresh your page';

export function useGraphQL<T extends OperationVariables, R = Query>( {
	queryKey,
	query,
	variables,
	subscription,
	context,
}: {
	queryKey: string | Array<string | undefined>,
	query: DocumentNode,
	variables?: T,
	subscription?: Record<string, string>,
	context?: any
}, options?: Omit<UseQueryOptions<R>, 'initialData'> & { initialData?: () => undefined } ) {
	const { sessionCompanyId } = useCompanyId();
	const keys = ( Array.isArray( queryKey ) ? queryKey : [ queryKey ] ).filter( Boolean ) as string[];
	const filter = omitBy( variables?.options?.filter, isNil );
	if ( isEmpty( filter ) ) delete variables?.options?.filter;
	const queryResult = useQuery<R, any>(
		keys.concat( sessionCompanyId || [], stringify( query ), stringify( variables ) ),
		() =>
			queryGraphQL<T, R>( {
				query,
				variables,
				context,
			} ),
		// options,
		{ placeholderData: {} as any, ...options },
	);
	const appEvent = useEvents();
	
	useEffect( () => {
		if ( !subscription || options?.enabled === false ) return;
		const listener = () => queryResult.refetch();
		const events: string[] = [];
		for ( const [ name, id ] of Object.entries( subscription ) ) {
			if ( !id ) continue;
			const event = `${name}:${id}`;
			appEvent.on( event, listener );
			events.push( event );
		}
		return () => {
			events.forEach( ( event ) => {
				appEvent.off( event, listener );
			} );
		};
	}, [ options?.enabled ] );
	
	return queryResult;
	// return { ...queryResult, data: queryResult.data === undefined ? {} as any : queryResult.data as Query | undefined };
}

export function useInfiniteGraphQL<
	T extends { options?: InputMaybe<{ limit?: InputMaybe<number> }> },
	D extends { id: string }>( {
	queryKey,
	query,
	variables,
	subscription, // TODO missing cursor based subscription from the backend
	select,
	context,
}: {
	queryKey: string | Array<string | undefined>,
	query: DocumentNode,
	select?: ( data: D[] ) => any,
	variables?: T,
	subscription?: Record<string, string>,
	context?: any
},
options?: Omit<UseInfiniteQueryOptions<InfiniteQuery>, 'initialData'> & { initialData?: () => undefined } ) {
	const limit = variables?.options?.limit || INFINITE_QUERY_LIMIT;
	const { sessionCompanyId } = useCompanyId();
	const keys = ( Array.isArray( queryKey ) ? queryKey : [ queryKey ] ).filter( Boolean ) as string[];
	const queryResult = useInfiniteQuery<InfiniteQuery, any>(
		keys.concat( sessionCompanyId || [], stringify( query ), stringify( variables ) ),
		async ( { pageParam = 0 } ) => {
			
			const response = await queryGraphQL( {
				query,
				variables: {
					...variables,
					options: {
						...variables?.options,
						limit : limit,
						offset: pageParam,
					},
				},
				context,
			} );
			const { items } = Object.values( response )[ 0 ] as { items: any[] };
			
			return {
				data     : response,
				pageParam: items.length === limit ? pageParam + limit : undefined,
			};
		},
		{
			getNextPageParam: ( lastPage, allPages ) => {
				const currentPageOffset = lastPage.pageParam || 0;
				
				const firstPageData = allPages?.[ 0 ]?.data
					? Object.values( allPages[ 0 ]?.data )?.[ 0 ] as PageData
					: undefined;
				const total = firstPageData?.count || 0;
				
				const fetchedItemsCount = allPages.reduce( ( acc, page ) => {
					const pageData = Object.values( page.data )?.[ 0 ] as PageData;
					const itemsLength = pageData.items?.length || 0;
					return acc + itemsLength;
				}, 0 );
				
				if ( fetchedItemsCount < total ) {
					return currentPageOffset + limit;
				}
				return undefined;
			},
			...options,
		},
	);
	
	const firstPageData = queryResult?.data?.pages?.[ 0 ]?.data as PageData;
	const totalCount = Object.values( firstPageData || {} )?.[ 0 ]?.count || 0;
	
	const flattenedData = useMemo<D[]>( () => {
		if ( !queryResult?.data?.pages ) return [] as D[];
		return pipe( queryResult.data.pages,
			map( ( { data } ) => Object.values( data as PageData || {} )?.[ 0 ]?.items ),
			flatten,
			uniqByFp<D>( 'id' ),
			select || identity,
		);
	}, [ queryResult?.data?.pages ] );
	
	return {
		...queryResult,
		flattenedData,
		count: totalCount,
	};
}
